import { useCallback } from 'react';
import { IconButton, Paper, Tooltip, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { mapNavigator } from '../services/MapNavigator';
import { useAppSelector } from '../store/hooks';

/**
 * ZoomControls component provides zoom in, zoom out, and zoom-to-extent
 * buttons for the map. Uses Material-UI IconButtons styled as a vertical
 * control group.
 *
 * **Validates: Requirements 30.1, 30.2, 30.6, 32.1, 32.2, 32.3, 32.4**
 */
const ZoomControls: React.FC = () => {
  const layers = useAppSelector((state) => state.layers.layers);

  const handleZoomIn = useCallback(() => {
    mapNavigator.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapNavigator.zoomOut();
  }, []);

  const handleZoomToExtent = useCallback(() => {
    mapNavigator.zoomToAllLayers(layers);
  }, [layers]);

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        borderRadius: 1,
        overflow: 'hidden',
      }}
      data-testid="zoom-controls"
    >
      <Tooltip title="Zoom In" placement="left">
        <IconButton
          onClick={handleZoomIn}
          size="small"
          aria-label="Zoom in"
          data-testid="zoom-in-btn"
          sx={{ borderRadius: 0, p: 1 }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider />
      <Tooltip title="Zoom Out" placement="left">
        <IconButton
          onClick={handleZoomOut}
          size="small"
          aria-label="Zoom out"
          data-testid="zoom-out-btn"
          sx={{ borderRadius: 0, p: 1 }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider />
      <Tooltip title="Zoom to All Layers" placement="left">
        <IconButton
          onClick={handleZoomToExtent}
          size="small"
          aria-label="Zoom to all layers"
          data-testid="zoom-extent-btn"
          sx={{ borderRadius: 0, p: 1 }}
        >
          <ZoomOutMapIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default ZoomControls;
